import { supabase } from '@/integrations/supabase/client';

const formatAttemptName = (name: string) => name.trim().replace(/\s+/g, ' ');
const normalizeAttemptName = (name: string) => formatAttemptName(name).toLocaleLowerCase();

export interface PremiumProgress {
  id: string;
  assessment_id: string;
  responses: Record<string, any>;
  current_step: number;
  personal_info: any;
  custom_name: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Create a brand new in-progress attempt for the given assessment.
 * Returns the new attempt id (the row id in premium_assessment_progress).
 */
export async function createPremiumAttempt(
  assessmentId: string,
  customName: string | null
): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await (supabase as any)
    .from('premium_assessment_progress')
    .insert({
      user_id: user.id,
      assessment_id: assessmentId,
      responses: {},
      current_step: 0,
      personal_info: null,
      custom_name: customName ? formatAttemptName(customName) : null,
    })
    .select('id')
    .single();

  if (error || !data) {
    return null;
  }
  return data.id as string;
}

/**
 * Save progress to a specific attempt (by row id).
 */
export async function saveAttemptProgress(
  attemptId: string,
  responses: Record<string, any>,
  currentStep?: number,
  personalInfo?: any,
  customName?: string | null
) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const payload: any = {
    responses,
    current_step: currentStep ?? 0,
    personal_info: personalInfo ?? null,
    updated_at: new Date().toISOString(),
  };
  if (customName !== undefined) {
    payload.custom_name = customName;
  }

  await (supabase as any)
    .from('premium_assessment_progress')
    .update(payload)
    .eq('id', attemptId)
    .eq('user_id', user.id);
}

/**
 * Update only the custom name of an attempt.
 */
export async function updateAttemptName(attemptId: string, customName: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await (supabase as any)
    .from('premium_assessment_progress')
    .update({ custom_name: formatAttemptName(customName), updated_at: new Date().toISOString() })
    .eq('id', attemptId)
    .eq('user_id', user.id);
}

export async function isAttemptNameTaken(name: string, excludeAttemptId?: string): Promise<boolean> {
  const formattedName = formatAttemptName(name);
  if (!formattedName) return false;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await (supabase as any)
    .from('premium_assessment_progress')
    .select('id, custom_name')
    .eq('user_id', user.id)
    .not('custom_name', 'is', null);

  if (error || !Array.isArray(data)) return false;

  const target = normalizeAttemptName(formattedName);
  return data.some((attempt: { id: string; custom_name: string | null }) => {
    if (!attempt.custom_name || attempt.id === excludeAttemptId) return false;
    return normalizeAttemptName(attempt.custom_name) === target;
  });
}

export async function getNextAvailableAttemptName(baseName: string, excludeAttemptId?: string): Promise<string> {
  const formattedBaseName = formatAttemptName(baseName);
  if (!formattedBaseName) return '';

  if (!(await isAttemptNameTaken(formattedBaseName, excludeAttemptId))) {
    return formattedBaseName;
  }

  let suffix = 2;
  let candidate = `${formattedBaseName} (${suffix})`;
  while (await isAttemptNameTaken(candidate, excludeAttemptId)) {
    suffix += 1;
    candidate = `${formattedBaseName} (${suffix})`;
  }

  return candidate;
}

/**
 * Load a single attempt by id.
 */
export async function loadAttempt(attemptId: string): Promise<PremiumProgress | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await (supabase as any)
    .from('premium_assessment_progress')
    .select('*')
    .eq('id', attemptId)
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) return null;
  return data;
}

/**
 * Delete a specific attempt by id.
 */
export async function deleteAttempt(attemptId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await (supabase as any)
    .from('premium_assessment_progress')
    .delete()
    .eq('id', attemptId)
    .eq('user_id', user.id);
}

/**
 * List all ongoing attempts (regardless of assessment) for the current user.
 */
export async function getAllPremiumProgress(): Promise<PremiumProgress[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await (supabase as any)
    .from('premium_assessment_progress')
    .select('*')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: false });

  if (error) return [];
  return data || [];
}

/**
 * List all ongoing attempts for a specific assessment.
 * Used to power the resume/start-new chooser dialog.
 */
export async function listOngoingAttemptsForAssessment(
  assessmentId: string
): Promise<PremiumProgress[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await (supabase as any)
    .from('premium_assessment_progress')
    .select('*')
    .eq('user_id', user.id)
    .eq('assessment_id', assessmentId)
    .order('updated_at', { ascending: false });

  if (error) return [];
  return data || [];
}

// ---------------------------------------------------------------------------
// Legacy compatibility helpers — kept so existing callers continue to work
// while we migrate them. New code should use the *Attempt* variants above.
// ---------------------------------------------------------------------------

/**
 * @deprecated Use loadAttempt(attemptId) instead. Returns the most recently
 * updated ongoing attempt for the assessment, if any.
 */
export async function loadPremiumProgress(assessmentId: string): Promise<PremiumProgress | null> {
  const attempts = await listOngoingAttemptsForAssessment(assessmentId);
  return attempts.length > 0 ? attempts[0] : null;
}

/**
 * @deprecated Use deleteAttempt(attemptId) instead. Deletes ALL ongoing
 * attempts for this assessment for the current user.
 */
export async function deletePremiumProgress(assessmentId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await (supabase as any)
    .from('premium_assessment_progress')
    .delete()
    .eq('user_id', user.id)
    .eq('assessment_id', assessmentId);
}

/**
 * @deprecated Premium flows should now create explicit attempts via
 * createPremiumAttempt + saveAttemptProgress. This helper is kept only for
 * non-premium callers (no-op for premium) so old code does not crash.
 */
export async function savePremiumProgress(
  _assessmentId: string,
  _responses: Record<string, any>,
  _currentStep?: number,
  _personalInfo?: any,
  _customName?: string | null
) {
  // Intentionally a no-op: the new model requires an attemptId.
  // Premium callers should use saveAttemptProgress directly.
}

/**
 * @deprecated Use updateAttemptName(attemptId, customName).
 */
export async function updatePremiumProgressName(
  assessmentId: string,
  customName: string
) {
  const attempts = await listOngoingAttemptsForAssessment(assessmentId);
  if (attempts.length > 0) {
    await updateAttemptName(attempts[0].id, customName);
  }
}
